import { AfterViewInit, Component, computed, HostListener, inject, OnDestroy, signal, Signal, ViewEncapsulation, WritableSignal } from '@angular/core';
import { TherapistClientStateService } from '@therapist/shared/services/state/therapist-client-state.service';
import { RouterLink } from '@angular/router';
import { TherapistClientDto } from '@therapist/shared/models/client/therapist-client.dto';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ExerciseActivityTypeEnum, ExerciseTemplateDto } from '@therapist/shared/models/exercise/exercise-template.dto';
import { ExerciseStateService } from '@therapist/shared/services/state/exercise-state.service';
import { SekeletonLoadingComponent } from '@therapist/shared/components/sekeleton-loading/sekeleton-loading.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { InputText } from 'primeng/inputtext';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { OverlayBadge } from 'primeng/overlaybadge';
import { WithMobileDetection } from '@base/mixins/with-mobile-detection.mixin';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ExerciseTemplateAddListComponent } from '../../components/exercise-template-add-list/exercise-template-add-list.component';
import {
  TherapyPlanAddExerciseConfirmationDialogComponent
} from '../../components/therapy-plan-add-exercise-confirmation-dialog/therapy-plan-add-exercise-confirmation-dialog.component';
import { JointStateService } from '@therapist/shared/services/state/joint-state.service';
import { TherapyPlanExerciseDto } from '@therapist/shared/models/exercise/therapy-plan-exercise.dto';
import { TherapyPlanStateService } from '@therapist/shared/services/state/therapy-plan-state.service';

@Component({
  selector: 'sana-tp-therapy-plan-add-exercises-page',
  imports: [
    FormsModule,
    ExerciseTemplateAddListComponent,
    SekeletonLoadingComponent,
    NgTemplateOutlet,
    Button,
    Tag,
    InputText,
    ReactiveFormsModule,
    OverlayBadge,
    RouterLink,
    NgClass
  ],
  templateUrl: './therapy-plan-add-or-edit-exercises-page.component.html',
  styleUrl: './therapy-plan-add-or-edit-exercises-page.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DialogService]
})
export class TherapyPlanAddOrEditExercisesPageComponent
  extends WithMobileDetection()
  implements AfterViewInit, OnDestroy {

  readonly #clientStateService = inject(TherapistClientStateService);
  readonly #jointStateService = inject(JointStateService);
  readonly #exerciseStateService = inject(ExerciseStateService);
  readonly #therapyPlanStateService = inject(TherapyPlanStateService);

  readonly #fb = inject(FormBuilder);
  readonly #dialogService = inject(DialogService);

  readonly searchForm = this.#fb.group({searchText: [null]});

  selectedClient: Signal<TherapistClientDto> = this.#clientStateService.getSelectedClient() as Signal<TherapistClientDto>;

  // alle Übungen zur gewählten Diagnose
  allExercises: Signal<ExerciseTemplateDto[]> = this.#exerciseStateService.getAllExercises();

  // alle Übungen zur gewählten Diagnose
  allExercisesFlatMappedWithVariants = this.#exerciseStateService.allExercisesWithVariantsFlat;

  // gefilterte Übungen zur gewählten Diagnose
  filteredExerciseList: WritableSignal<ExerciseTemplateDto[] | null> = signal(this.allExercises());

  // die Übungen des vorherigen Therapieplans
  previousTherapyPlanExerciseList = computed<TherapyPlanExerciseDto[]>(() => {
    const plan = this.#therapyPlanStateService.previousTherapyPlan();
    return plan?.therapyPlanExercises ?? [];
  });

  // die ausgewählten Übungen für diesen Therapieplan
  selectedExerciseList: Signal<ExerciseTemplateDto[] | null> = this.#exerciseStateService.getSelectedExercises();

  // die ausgewählten Diagnosen für diesen Therapieplan
  selectedJointNames = this.#jointStateService.getSelectedJointNames();

  isClientLoaded: Signal<boolean> = this.#clientStateService.getIsClientLoaded();
  isExerciseListLoaded: Signal<boolean> = this.#exerciseStateService.getIsExerciseListLoaded();
  isPreviousTherapyPlanApplied: Signal<boolean> = this.#therapyPlanStateService.getIsPreviousTherapyPlanApplied();

  appliedActivityTypeFilters: ExerciseActivityTypeEnum[] = [];
  isPreviousSectionExerciseFilterSelected = false;

  searchText = () => this.searchForm.controls.searchText.value;

  clientId: string | null = null;

  ref: DynamicDialogRef | undefined;
  componentDestroyed$ = new Subject();

  hasScrollPadding = signal(false);

  // jene Übungen des vorherigen Therapieplans, die auch auf die Gelenksauswahl des neuen Therapieplans passen
  previousExercisesApplicableList = computed(() => {
    console.log('*****************************************************************');
    console.log('allExercises', this.allExercisesFlatMappedWithVariants());
    console.log('previousTherapyPlanExerciseList', this.previousTherapyPlanExerciseList());

    const allExercises = this.allExercisesFlatMappedWithVariants();
    const previous = this.previousTherapyPlanExerciseList();

    return previous.filter(prev =>
      allExercises.some(all => all.id === prev.templateId)
    );
  })

  addAllClicked = false;
  addPreviousClicked = false;

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateScrollState();
  }

  constructor() {
    super();

    this.searchForm.controls.searchText
      .valueChanges
      .pipe(
        takeUntilDestroyed(),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(searchText => this.applyTextFilter(searchText));

    // the initial value of allExercises might not be loaded yet when it is assigned to filteredExerciseList
    toObservable(this.isExerciseListLoaded)
      .pipe(takeUntilDestroyed())
      .subscribe(isLoaded => {
        if (isLoaded && this.filteredExerciseList()?.length === 0) {
          this.filteredExerciseList.set([...this.allExercises()]);
        }
      });
  }

  ngAfterViewInit() {
    this.updateScrollState();
  }

  addExerciseToPlan(exercise: ExerciseTemplateDto) {
    this.#exerciseStateService.addSelectedExercise(exercise);
  }

  addAllExerciseToPlan(exerciseList: ExerciseTemplateDto[] | null) {
    if (exerciseList === null || this.addAllClicked) {
      return;
    }

    exerciseList.forEach(exercise => this.#exerciseStateService.addSelectedExercise(exercise));
    this.addAllClicked = true;
  }

  removeExerciseFromPlan(exercise: ExerciseTemplateDto) {
    this.#exerciseStateService.removeSelectedExercise(exercise.id);
  }

  activityTypeFilterClicked(type: ExerciseActivityTypeEnum): void {
    if (this.isActivityFilterAdded(type)) {
      this.removeActivityTypeFilter(type);
      return;
    }

    if (this.isPreviousSectionExerciseFilterSelected) {
      this.isPreviousSectionExerciseFilterSelected = false;
    }

    this.removeAllActivityTypeFilters();
    this.addActivityTypeFilter(type);
  }

  private addActivityTypeFilter(type: ExerciseActivityTypeEnum): void {
    this.appliedActivityTypeFilters.push(type);
    this.applyActivityTypeFilters(this.appliedActivityTypeFilters);
  }

  private removeActivityTypeFilter(type: ExerciseActivityTypeEnum): void {
    this.appliedActivityTypeFilters = this.appliedActivityTypeFilters.filter(t => t !== type);
    this.applyActivityTypeFilters(this.appliedActivityTypeFilters);
  }

  private removeAllActivityTypeFilters(): void {
    this.appliedActivityTypeFilters = [];
  }

  isActivityFilterAdded(type: ExerciseActivityTypeEnum): boolean {
    return this.appliedActivityTypeFilters.includes(type);
  }

  private applyActivityTypeFilters(filters: ExerciseActivityTypeEnum[]) {
    this.filteredExerciseList.set(this.applyAllActiveFilters(this.allExercises()!, this.searchText(), filters));
  }

  private applyTextFilter(searchText: string | null) {
    this.filteredExerciseList.set(this.applyAllActiveFilters(this.allExercises()!, searchText, this.appliedActivityTypeFilters));
  }

  private applyAllActiveFilters(exercises: ExerciseTemplateDto[], searchText: string | null, activityTypeFilters: ExerciseActivityTypeEnum[]): ExerciseTemplateDto[] {
    let result = [...exercises];

    // check if search text filter is active, if so, filter on it
    result = this.filterBySearchText(result, searchText);

    // check if activity type filter is active, if so, filter on it
    if (activityTypeFilters.length !== 0) {
      result = result.filter(exercise => activityTypeFilters.includes(exercise.activityType));
    }

    return result;
  }

  private filterBySearchText(exercises: ExerciseTemplateDto[], searchText: string | null): ExerciseTemplateDto[] {
    if (!!searchText && searchText.trim().length !== 0) {
      return [...exercises.filter(exercise => exercise.title.toLowerCase().includes(searchText!.toLowerCase()))];
    }

    return exercises;
  }

  private updateScrollState(): void {
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    const next = y >= 36;

    if (next !== this.hasScrollPadding()) {
      this.hasScrollPadding.set(next);
    }
  }

  openTherapyPlanExerciseConfirmationDialog(): void {
    this.ref = this.#dialogService.open(TherapyPlanAddExerciseConfirmationDialogComponent, {
      header: 'Übungen in diesem Therapieplan',
      width: '33vw',
      style: {'min-height': '40vh', 'max-height': '80vh'},
      modal: true,
      breakpoints: {
        '1280px': '50vw',
        '960px': '75vw',
        '640px': '90vw'
      },
      closable: true,
      dismissableMask: false,
      closeOnEscape: true
    });
  }

  addPreviousExercisesToPlan(): void {
    if (this.addPreviousClicked) {
      return;
    }

    this.previousExercisesApplicableList().forEach(exercise => {
      let template = this.allExercisesFlatMappedWithVariants().find(exc => exc.id === exercise.templateId);

      if (!template) {
        console.warn(`Could not add exercise ${exercise.id} with title ${exercise.title} from previousPlan, because it was not found in the allExercisesFlatMappedWithVariants.`);
        return;
      }

      this.#exerciseStateService.addSelectedExercise(template!);
    });

    this.addPreviousClicked = true;
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(null);
    this.componentDestroyed$.complete();

    this.ref?.close();
  }

  readonly ExerciseActivityTypeEnum = ExerciseActivityTypeEnum;
  protected readonly length = length;
}
